import type { RootState } from "~/app/store";
import { selectProductDescriptor } from "~/domains/product/redux/selectors";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import {
  isPlainLineItem,
  type CartGrouping,
  type GroupedCartEntry,
  type SubLineItem,
} from "~/features/cart/cart/models/CartData";
import { type selectPackageEntryMap } from "~/features/cart/cart/redux/selectors/cartSelectors";
import type {
  AlternateRowViewModel,
  CartEntryRowViewModel,
  ProductTableMode,
  ProductTableRowViewModel,
  ProductTableViewModel,
} from "~/features/cart/cartV2/models/ProductTableViewModel";
import { buildCartEntryLines } from "~/features/cart/cartV2/utils/buildCartEntryLines";
import { buildCartEntrySummary } from "~/features/cart/cartV2/utils/buildCartEntrySummary";
import { buildEntryRowId, buildTableId } from "~/features/cart/cartV2/utils/buildTableId";
import type { ResolvedGroupedCartEntryV2 } from "~/features/cart/cartV2/utils/selectResolvedGroupedEntryV2";

function getQuoteNumber(group: CartGrouping): string | undefined {
  const candidate = (group as { quoteNumber?: unknown }).quoteNumber;
  return typeof candidate === "string" ? candidate : undefined;
}

function buildGroupingViewModel(entry: GroupedCartEntry): CartEntryRowViewModel["grouping"] {
  return {
    cartEntryId: entry.cartEntryId,
    packGroups: entry.packGroups.map((packGroup) => ({
      lineItemIds: packGroup.packEntryIds,
    })),
  };
}

export function buildSubLineItemsByParentId(
  entry: ResolvedGroupedCartEntryV2,
  packageEntryMap: ReturnType<typeof selectPackageEntryMap>,
): Record<number, SubLineItem[]> {
  return entry.lineItems.reduce<Record<number, SubLineItem[]>>((accumulator, lineItem) => {
    if (!isPlainLineItem(lineItem) || lineItem.optionalPartIds.length === 0) {
      return accumulator;
    }

    accumulator[lineItem.id] = lineItem.optionalPartIds
      .map((optionalPartId) => packageEntryMap[optionalPartId])
      .filter((optionalPart): optionalPart is SubLineItem => !!optionalPart);

    return accumulator;
  }, {});
}

export function collectAllSubLineItems(
  entries: ResolvedGroupedCartEntryV2[],
  packageEntryMap: ReturnType<typeof selectPackageEntryMap>,
): SubLineItem[] {
  return entries.flatMap((entry) => Object.values(buildSubLineItemsByParentId(entry, packageEntryMap)).flat());
}

export function buildOptionalPartDescriptorMap(
  state: RootState,
  subLineItems: SubLineItem[],
): Record<number, ProductDescriptor> {
  const map: Record<number, ProductDescriptor> = {};
  for (const sub of subLineItems) {
    map[sub.id] = selectProductDescriptor(state, {
      productCode: sub.productCode,
      priceReference: sub.priceReference,
    });
  }
  return map;
}

export function compareOptionalPartDescriptorMaps(
  prev: Record<number, ProductDescriptor>,
  next: Record<number, ProductDescriptor>,
): boolean {
  const prevKeys = Object.keys(prev);
  if (prevKeys.length !== Object.keys(next).length) {
    return false;
  }
  return prevKeys.every((key) => prev[key as unknown as number] === next[key as unknown as number]);
}

function buildEntryRowViewModel(args: {
  entry: ResolvedGroupedCartEntryV2;
  mode: ProductTableMode;
  packageEntryMap: ReturnType<typeof selectPackageEntryMap>;
  optionalPartDescriptors: Record<number, ProductDescriptor>;
}): CartEntryRowViewModel {
  const { entry, mode, packageEntryMap, optionalPartDescriptors } = args;

  return {
    kind: "entry",
    rowId: buildEntryRowId(entry.cartEntryId),
    cartEntryId: entry.cartEntryId,
    grouping: buildGroupingViewModel(entry),
    descriptors: entry.descriptors,
    mainDescriptor: entry.mainDescriptor,
    mainDescriptorSelection: entry.mainDescriptorSelection,
    summary: buildCartEntrySummary(entry),
    details: buildCartEntryLines({
      entry,
      mode,
      subLineItemsByParentId: buildSubLineItemsByParentId(entry, packageEntryMap),
      optionalPartDescriptors,
    }),
  };
}

function buildRows(args: {
  entryRows: CartEntryRowViewModel[];
  alternateRows: AlternateRowViewModel[];
}): ProductTableRowViewModel[] {
  const { entryRows, alternateRows } = args;

  if (entryRows.length === 0) {
    return alternateRows;
  }

  const rows: ProductTableRowViewModel[] = [];
  const alternateRowsByAnchor = alternateRows.reduce<Record<string, AlternateRowViewModel[]>>((accumulator, row) => {
    const anchorKey = String(row.anchorCartEntryId);

    if (!accumulator[anchorKey]) {
      accumulator[anchorKey] = [];
    }

    accumulator[anchorKey].push(row);
    return accumulator;
  }, {});

  entryRows.forEach((entryRow) => {
    rows.push(...(alternateRowsByAnchor[String(entryRow.cartEntryId)] ?? []));
    rows.push(entryRow);
  });

  return rows;
}

export interface BuildProductTableViewModelArgs {
  group: CartGrouping;
  mode: ProductTableMode;
  resolvedEntries: ResolvedGroupedCartEntryV2[];
  packageEntryMap: ReturnType<typeof selectPackageEntryMap>;
  optionalPartDescriptors: Record<number, ProductDescriptor>;
  alternateRows: AlternateRowViewModel[];
}

export function buildProductTableViewModel({
  group,
  mode,
  resolvedEntries,
  packageEntryMap,
  optionalPartDescriptors,
  alternateRows,
}: BuildProductTableViewModelArgs): ProductTableViewModel {
  const tableId = buildTableId(group);
  const quoteNumber = getQuoteNumber(group);

  const entryRows = resolvedEntries.map((entry) =>
    buildEntryRowViewModel({ entry, mode, packageEntryMap, optionalPartDescriptors }),
  );

  const rows = buildRows({ entryRows, alternateRows });

  return {
    tableId,
    mode,
    groupType: group.type,
    quoteNumber,
    rows,
  };
}
